import { useState, useCallback } from 'react'

export interface UIState {
  openSort: boolean
  openFilter: boolean
  openMore: boolean
  openSelect: boolean
  openDisplayedData: boolean
  showCertificatePicker: boolean
  showNfcScan: boolean
  showDatePicker: boolean
  isSearchInputFocused: boolean
  desktopSearchFocused: boolean
}

export interface SortState {
  mobileSortStep: 'pick' | 'configure'
  mobileSortField: string
  mobileSortDir: 'ASC' | 'DESC'
  additionalSorts: Array<{ field: string; direction: 'ASC' | 'DESC' }>
}

const DEFAULT_UI_STATE: UIState = {
  openSort: false,
  openFilter: false,
  openMore: false,
  openSelect: false,
  openDisplayedData: false,
  showCertificatePicker: false,
  showNfcScan: false,
  showDatePicker: false,
  isSearchInputFocused: false,
  desktopSearchFocused: false,
}

const DEFAULT_SORT_STATE: SortState = {
  mobileSortStep: 'pick',
  mobileSortField: 'name',
  mobileSortDir: 'ASC',
  additionalSorts: [],
}

export const useCertificateUI = () => {
  const [uiState, setUiState] = useState<UIState>(DEFAULT_UI_STATE)
  const [sortState, setSortState] = useState<SortState>(DEFAULT_SORT_STATE)

  // UI state setters
  const setOpenSort = useCallback((open: boolean) => {
    setUiState(prev => ({ ...prev, openSort: open }))
  }, [])

  const setOpenFilter = useCallback((open: boolean) => {
    setUiState(prev => ({ ...prev, openFilter: open }))
  }, [])

  const setOpenMore = useCallback((open: boolean) => {
    setUiState(prev => ({ ...prev, openMore: open }))
  }, [])

  const setOpenSelect = useCallback((open: boolean) => {
    setUiState(prev => ({ ...prev, openSelect: open }))
  }, [])

  /*
   * Flips the selection bar from its PREVIOUS state, rather than from a value the
   * caller read.
   *
   * The caller's version — `if (uiState.openSelect) close() else open()` — opened
   * the bar and then would not close it, because the handler closes over the
   * `uiState` of the render that created it. Pressing Select re-ran the stale
   * branch and re-opened what was already open. A functional update reads the
   * state React actually holds, so it cannot drift from what is on screen.
   */
  const toggleSelect = useCallback(() => {
    setUiState(prev => ({ ...prev, openSelect: !prev.openSelect }))
  }, [])

  const setOpenDisplayedData = useCallback((open: boolean) => {
    setUiState(prev => ({ ...prev, openDisplayedData: open }))
  }, [])

  // Sort state setters
  const setMobileSortStep = useCallback((step: 'pick' | 'configure') => {
    setSortState(prev => ({ ...prev, mobileSortStep: step }))
  }, [])

  const setMobileSortField = useCallback((field: string) => {
    setSortState(prev => ({ ...prev, mobileSortField: field }))
  }, [])

  const setMobileSortDir = useCallback((dir: 'ASC' | 'DESC') => {
    setSortState(prev => ({ ...prev, mobileSortDir: dir }))
  }, [])

  const setAdditionalSorts = useCallback((sorts: Array<{ field: string; direction: 'ASC' | 'DESC' }>) => {
    setSortState(prev => ({ ...prev, additionalSorts: sorts }))
  }, [])

  // Reset functions
  const resetUIState = useCallback(() => {
    setUiState(DEFAULT_UI_STATE)
  }, [])

  const resetSortState = useCallback(() => {
    setSortState(DEFAULT_SORT_STATE)
  }, [])

  return {
    uiState,
    sortState,
    
    setOpenSort,
    setOpenFilter,
    setOpenMore,
    setOpenSelect,
    toggleSelect,
    setOpenDisplayedData,
    
    setMobileSortStep,
    setMobileSortField,
    setMobileSortDir,
    setAdditionalSorts,
    
    resetUIState,
    resetSortState,
  }
}
